import { HolderAccount, Card as UpCardType } from '@odiupsk/up-api-client'
import React, { useContext, useMemo } from 'react'
import { Trans, t } from '@lingui/macro'
import Router from 'next/router'
import format from 'date-fns/format'
import { observer } from 'mobx-react-lite'
import { trace } from 'mobx'
import {
  Bar,
  BarItem,
  Button,
  ButtonDropdown,
  ButtonLayout,
  ButtonLink,
  ButtonPrimary,
  Card,
  CardContent,
  DropdownItem,
  Grid,
  GridCol,
  Icon,
  Tooltip,
} from '../../common/ui-kit'
import { EmployeeCtx, EmployeeStore, employeeStore } from '../EmployeeStore'
import { _, i18n } from '../../common/utils/intl'
import { formatDate, formatDateMonth, formatMoney } from '../../common/utils/format'

import { ActivateDeactivateCardModal } from '../../common/modules/dash-board/ActivateDeactivateCardModal'
import { CardsStore } from './CardsStore'
import { ChangePinModal } from '../../common/modules/modals/ChangePinModal'
import { CreditExpirationModal } from './CreditExpirationModal'
import { IconBook } from '../../common/ui-kit/icons/IconBook'
import { ImgFromAPI } from '../../common/utils/Img'
import { LINKS } from '../../config/constants'
import { NfcCodeModal } from './NfcCodeModal'
import { SendCreditModal } from '../../common/modules/dash-board/SendCreditModal'
import { useDialog } from '../../common/utils/hooks/useDialog'

const SendCredit: React.FC<{
  isDisabled: boolean
  cardId: string
  holderId: number
  holderAccountId: string
  empStore: EmployeeStore
  employeeId: string
}> = observer(({ cardId, isDisabled, holderId, holderAccountId, empStore, employeeId }) => {
  const d = useDialog()

  return (
    <>
      <ButtonLayout>
        <ButtonPrimary isDisabled={isDisabled} onClick={d.open}>
          <Trans>Poslať kredit</Trans>
        </ButtonPrimary>
      </ButtonLayout>
      {d.isOpen && (
        <SendCreditModal
          close={d.close}
          cardId={cardId}
          holderId={holderId}
          holderAccountId={holderAccountId}
          onSuccess={() => {
            empStore.fetchEmployee(employeeId, true)
          }}
        />
      )}
    </>
  )
})

const ChangePin: React.FC = observer(() => {
  const d = useDialog()

  return (
    <>
      <Button onClick={d.open}>
        <Trans>Zabudol som PIN</Trans>
      </Button>
      {d.isOpen && <ChangePinModal close={d.close} />}
    </>
  )
})

const CreditExpiration: React.FC<{
  holderAccount: HolderAccount
}> = observer(({ holderAccount }) => {
  const d = useDialog()

  return (
    <>
      <ButtonLink space="nospace" onClick={d.open}>
        <Trans>Platnosť kreditu</Trans>
      </ButtonLink>
      {d.isOpen && <CreditExpirationModal close={d.close} holderAccount={holderAccount} />}
    </>
  )
})

const NfcCode: React.FC<{
  card: UpCardType
}> = observer(({ card }) => {
  const nfcModal = useDialog()

  return (
    <>
      <ButtonLink space="nospace" onClick={nfcModal.open}>
        <Trans>NFC Android aktivácia</Trans>
      </ButtonLink>

      {nfcModal.isOpen && <NfcCodeModal close={nfcModal.close} card={card} />}
    </>
  )
})

const Deactivate: React.FC<{ cards: CardsStore }> = observer(({ cards }) => {
  const d = useDialog()
  return (
    <>
      <Button onClick={d.open}>
        <Trans>Deaktivovať kartu</Trans>
      </Button>
      {d.isOpen && <ActivateDeactivateCardModal close={d.close} type="block" cards={cards} />}
    </>
  )
})

const Activate: React.FC<{ cards: CardsStore }> = observer(({ cards }) => {
  const d = useDialog()
  return (
    <>
      <Button onClick={d.open}>
        <Trans>Aktivovať kartu</Trans>
      </Button>
      {d.isOpen && <ActivateDeactivateCardModal close={d.close} type="activate" cards={cards} />}
    </>
  )
})

export const Sidebar = observer((props: { cards: CardsStore }) => {
  trace()

  const { cards } = props
  const { selectedCard } = cards
  const employee = useContext(EmployeeCtx)

  const selectedHolderAccount = useMemo(
    () =>
      (employee.holder_accounts || []).find(h => {
        return !!(selectedCard && selectedCard.holder_account_id === h.id)
      }),
    [employee.holder_accounts, selectedCard]
  )

  const unlimitedCreditExpiration =
    !!cards.cardCreditsSummary &&
    cards.cardCreditsSummary.credits?.every(item => item.unlimited_validity)

  const userHasCard = employee.holder_accounts?.some(ha => ha.cards?.length)
  const CardInformationSummary = () => (
    <CardContent>
      <Grid>
        <GridCol className="h5">
          {employee.last_name} {employee.first_name}
        </GridCol>
        {employee.holder_id && (
          <>
            <GridCol className="text-color-grey-600">
              <Trans>Číslo držiteľa karty Up</Trans>:
            </GridCol>
            <GridCol className="mb-large">
              <strong>{employee.holder_id}</strong>
            </GridCol>
          </>
        )}

        {(selectedHolderAccount?.last_recharge_date ||
          employee?.holder_accounts?.[0]?.last_recharge_date) && (
          <>
            <GridCol className="text-color-grey-600">
              <Trans>Dátum posledného dobitia</Trans>:
            </GridCol>
            <GridCol className="mb-large">
              <strong>
                {formatDate(
                  selectedHolderAccount?.last_recharge_date ||
                    (employee?.holder_accounts?.[0]?.last_recharge_date as string)
                )}
              </strong>
            </GridCol>
          </>
        )}

        {employee?.holder_accounts?.[0]?.amount! >= 0 ? (
          <>
            <GridCol className="text-color-grey-600">
              <Trans>Aktuálny zostatok</Trans>:
            </GridCol>
            <GridCol className="h2 mb-none">
              {employee?.holder_accounts?.[0]?.amount &&
                formatMoney(employee.holder_accounts[0].amount)}{' '}
              {selectedHolderAccount?.currency
                ? selectedHolderAccount?.currency.name
                : employee.holder_accounts?.[0]?.currency?.name || '€'}
            </GridCol>
          </>
        ) : null}

        {(selectedHolderAccount?.id || employee?.holder_accounts?.[0]) && (
          <>
            {unlimitedCreditExpiration ? (
              <GridCol className="text-bold text-color-grey-700 text-small mb-large">
                <Trans>Neobmedzená platnosť kreditu</Trans>
              </GridCol>
            ) : (
              <GridCol>
                <CreditExpiration
                  holderAccount={
                    selectedHolderAccount ? selectedHolderAccount! : employee?.holder_accounts?.[0]!
                  }
                />
              </GridCol>
            )}
          </>
        )}
      </Grid>

      {(selectedHolderAccount?.id || employee?.holder_accounts?.[0]) && (
        <SendCredit
          holderAccountId={cards.cards?.[0]?.holder_account_id}
          holderId={(selectedCard as any)?.holder_id}
          isDisabled={!!selectedCard?.gdf}
          cardId={selectedCard?.id!}
          empStore={employeeStore}
          employeeId={employee.id}
        />
      )}
    </CardContent>
  )

  return (
    <React.Fragment>
      <h2 className="h5" style={{ marginLeft: `${cards.cards.length > 1 ? 0 : 5}px` }}>
        <Trans>Moja karta</Trans>
      </h2>

      {cards.cards.length > 1 && (
        <>
          <div className="text-bold text-small text-transform-upper text-color-grey mb-tiny">
            <Trans>Pracujete s kartou</Trans>
          </div>
          <ButtonLayout>
            <ButtonDropdown
              type="primary"
              content={selectedCard?.pan}
              icon
              spaceBetween
              // defaultValue={selectedCard.id}
              data-testid="btn-cards-switcher">
              {cards.cards.map(i => {
                let validToString = '??/??'
                if (i.valid_to) {
                  validToString = formatDateMonth(i.valid_to)
                }

                return (
                  <DropdownItem
                    key={i.id}
                    onClick={() => {
                      Router.replace(
                        {
                          pathname: '/end-user',
                          query: { card_id: i.id },
                        },
                        undefined
                      )
                    }}>
                    {`Karta ${validToString}`}
                  </DropdownItem>
                )
              })}
            </ButtonDropdown>
          </ButtonLayout>
        </>
      )}

      {!userHasCard ? (
        <Card>
          <CardInformationSummary />
        </Card>
      ) : (
        <Card>
          <CardInformationSummary />

          <CardContent>
            <Grid>
              <GridCol className="mb-large">
                <div
                  style={{
                    position: 'absolute',
                    top: '0.3rem',
                    right: '0rem',
                  }}>
                  <Tooltip
                    content={<Icon name="info" size="medium" className="text-color-theme-500" />}
                    dialog={_(t`Náhľad karty je len informatívny`)}
                  />
                </div>

                {selectedCard?.id && <ImgFromAPI src={`/cards/${selectedCard?.id}/preview`} />}
              </GridCol>
              <GridCol className="text-color-grey-600">
                <Trans>Číslo karty</Trans>:
              </GridCol>
              <GridCol className="mb-large">
                <Bar space="small" className="mb-none">
                  <BarItem className="h5">{selectedCard?.pan}</BarItem>
                  <BarItem>
                    <div>
                      <Tooltip
                        content={
                          <Icon name="info" size="medium" className="text-color-theme-500" />
                        }
                        dialog={_(t`Číslo karty nemožno zobrazovať`)}
                      />
                    </div>
                  </BarItem>
                </Bar>
              </GridCol>
              <GridCol className="text-color-grey-600">
                <Trans>Platnosť karty do</Trans>:
              </GridCol>
              <GridCol className="h5 mb-large">
                {selectedCard?.valid_to && format(selectedCard?.valid_to, 'MM/YY')}
              </GridCol>
              <GridCol className="text-color-grey-600">
                <Trans>Status karty</Trans>:
              </GridCol>
              <GridCol className="h5 mb-large">{selectedCard?.status_text}</GridCol>
              {/* @ts-ignore */}
              {selectedCard?.nfc_code && (
                <GridCol>
                  <NfcCode card={selectedCard!} />
                </GridCol>
              )}
              <GridCol>
                <ButtonLayout>
                  {selectedCard?.can_block && <Deactivate cards={cards} />}
                  {selectedCard?.can_activate_by_holder && <Activate cards={cards} />}
                  <ChangePin />
                </ButtonLayout>
              </GridCol>
            </Grid>
          </CardContent>
        </Card>
      )}
      <Card>
        <CardContent size="condensed">
          <a
            className="btn btn--small btn--condensed btn--link mb-none"
            href={i18n.t`${LINKS.tutorials.dashboardEndUser}`}
            target="_blank"
            style={{ display: 'flex', alignItems: 'center' }}>
            <IconBook style={{ marginRight: '12px', marginTop: '-2px' }} />
            <Trans>Zobraziť manuál</Trans>
          </a>
        </CardContent>
      </Card>
    </React.Fragment>
  )
})
